import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldHint } from '../components/ui/Field';
import { Input, InputGroup, InputAddon } from '../components/ui/Input';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';
import { AuthShell } from '../components/layout/AuthShell';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address')
});

const passwordSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters long'),
  password_confirmation: z.string().min(1, 'Please confirm your password')
}).refine((data) => data.password === data.password_confirmation, {
  message: 'Passwords do not match',
  path: ['password_confirmation']
});

type EmailFormValues = z.infer<typeof emailSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const { forgotPassword, resetPassword } = useAuth();
  const { translate } = useLanguage();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const emailFromUrl = searchParams.get('email') || '';
  const isResetMode = !!token;

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: emailErrors }
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' }
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors }
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', password_confirmation: '' }
  });

  const onSubmitEmail = async (data: EmailFormValues) => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await forgotPassword({ email: data.email });
      setSuccess(response.message || 'Password reset link has been sent to your email address');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send reset link. Please check your email and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitPassword = async (data: PasswordFormValues) => {
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const resetData = {
        email: emailFromUrl,
        password: data.password,
        password_confirmation: data.password_confirmation,
        token
      };

      const response = await resetPassword(resetData);
      setSuccess(response.message || 'Your password has been successfully reset');

      setTimeout(() => {
        navigate('/login', { state: { message: 'Password reset successful. Please login with your new password.' } });
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const signInLink = (
    <>
      {translate('return-to-login')}{' — '}
      <RouterLink to="/login" style={{ color: 'var(--brand-blue)', fontWeight: 600, textDecoration: 'none' }}>
        {translate('sign-in')}
      </RouterLink>
    </>
  );

  return (
    <AuthShell
      title={isResetMode ? 'Set new password' : translate('reset-your-password')}
      sub={isResetMode ? 'Please enter your new password below.' : translate('well-email-secure-link')}
      footer={signInLink}
    >
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">{error}</Callout>
        </div>
      )}

      {success && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="ok">{success}</Callout>
        </div>
      )}

      {isResetMode ? (
        <form onSubmit={handleSubmitPassword(onSubmitPassword)} noValidate>
          {emailFromUrl && (
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--brand-blue)', fontWeight: 500 }}>
              {emailFromUrl}
            </p>
          )}

          <div className="field">
            <label className="field-label" htmlFor="password">{translate('new-password')}</label>
            <InputGroup>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="input-lg"
                {...registerPassword('password')}
              />
              <InputAddon>
                <button
                  type="button"
                  style={{
                    background: 'transparent',
                    border: 0,
                    padding: '0 4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: 'var(--ink-3)',
                  }}
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={translate(showPassword ? 'hide-password' : 'show-password')}
                >
                  <Icon name="eye" size={16} />
                </button>
              </InputAddon>
            </InputGroup>
            {passwordErrors.password && (
              <FieldHint role="alert" style={{ color: 'var(--err)' }}>
                {passwordErrors.password.message}
              </FieldHint>
            )}
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <label className="field-label" htmlFor="password_confirmation">{translate('confirm-password')}</label>
            <InputGroup>
              <Input
                id="password_confirmation"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="input-lg"
                {...registerPassword('password_confirmation')}
              />
              <InputAddon>
                <button
                  type="button"
                  style={{
                    background: 'transparent',
                    border: 0,
                    padding: '0 4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: 'var(--ink-3)',
                  }}
                  onClick={() => setShowConfirmPassword(v => !v)}
                  aria-label={translate(showConfirmPassword ? 'hide-password' : 'show-password')}
                >
                  <Icon name="eye" size={16} />
                </button>
              </InputAddon>
            </InputGroup>
            {passwordErrors.password_confirmation && (
              <FieldHint role="alert" style={{ color: 'var(--err)' }}>
                {passwordErrors.password_confirmation.message}
              </FieldHint>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block btn-lg"
            style={{ marginTop: 18 }}
            disabled={isLoading}
          >
            {isLoading ? 'Resetting…' : translate('reset-password')}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSubmitEmail(onSubmitEmail)} noValidate>
          <div className="field">
            <label className="field-label" htmlFor="email">{translate('email-address')}</label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              className="input-lg"
              {...registerEmail('email')}
            />
            {emailErrors.email && (
              <FieldHint role="alert" style={{ color: 'var(--err)' }}>
                {emailErrors.email.message}
              </FieldHint>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block btn-lg"
            style={{ marginTop: 18 }}
            disabled={isLoading}
          >
            {isLoading ? 'Sending…' : translate('send-reset-link')}
          </button>
        </form>
      )}
    </AuthShell>
  );
};

export default ResetPasswordPage;
